import { formatCalendarDate } from "../../utils/date";
import { bullseyeAwardCutoff, rankBullseyeRows, hasBullseyeScoreData } from "./bullseyePostedRanking";
import { bullseyeTieContext } from "./bullseyeTieBreaks";
import { normalizeResultClass, competingResultClass, separateClassGroups, classAwardCount } from "./bullseyeResultGrouping";

export function formatDate(value) {
  if (!value) {
    return "-";
  }

  return formatCalendarDate(value);
}

export function buildLocationLabel(match) {
  const primary = String(match?.location || "").trim();
  if (primary) {
    return primary;
  }

  const city = String(match?.city || "").trim();
  const state = String(match?.state || "").trim();
  const range = String(match?.rangeName || "").trim();

  if (city && state) return `${city}, ${state}`;
  if (range && state) return `${range}, ${state}`;
  if (range) return range;
  if (state) return state;
  return "Location not set";
}

export function participationCountsText(count, twentyTwoOnlyCount) {
  return `${count} ${count === 1 ? "competitor" : "competitors"} · includes ${twentyTwoOnlyCount} .22 Only`;
}

export function sightDivisionLabel(value) {
  const division = String(value || "").trim().toLowerCase();
  return division === "open" ? "Open division" : division === "metallic" ? "Metallic division" : "";
}

const BULLSEYE_CLASS_ORDER = ["High Master", "Master", "Expert", "Sharpshooter", "Marksman", "Unclassified"];

const BULLSEYE_OPEN_AWARD_LABELS = ["Match Winner", "Second Place", "Third Place"];

const BULLSEYE_CLASS_AWARD_LABELS = ["First", "Second", "Third"];

const BULLSEYE_CATEGORY_ORDER = ["Woman", "Civilian", "Police", "Service", "Veteran", "Junior", "Intermediate Junior", "Sub-Junior", "Senior", "Grand Senior", "Metallic", "Open", "22 Only"];

const BULLSEYE_CATEGORY_LABELS = new Map([
  ["junior", "Junior"],
  ["intermediate_junior", "Intermediate Junior"],
  ["sub_junior", "Sub-Junior"],
  ["senior", "Senior"],
  ["grand_senior", "Grand Senior"],
  ["grand senior", "Grand Senior"],
  ["women", "Woman"],
  ["woman", "Woman"],
  ["lady", "Woman"],
  ["veteran", "Veteran"],
  ["civilian", "Civilian"],
  ["police", "Police"],
  ["service", "Service"],
  ["twenty_two_only_aggregate", "22 Only"],
  ["twenty two only aggregate", "22 Only"],
  ["22_only", "22 Only"],
  ["22 only", "22 Only"],
  [".22 only", "22 Only"],
  [".22 caliber only", "22 Only"],
  ["22 caliber only", "22 Only"],
  ["22 calibre only", "22 Only"],
]);

export function numberOrZero(value) {
  const numericValue = Number(value || 0);
  return Number.isFinite(numericValue) ? numericValue : 0;
}

function normalizeBullseyeCourseName(value) {
  const label = String(value || "").trim();
  const lower = label.toLowerCase();
  if (lower.includes("national match") || lower === "nmc") return "National Match Course";
  if (lower.includes("slow")) return "Slow Fire";
  if (lower.includes("timed")) return "Timed Fire";
  if (lower.includes("rapid")) return "Rapid Fire";
  return label || "Course";
}

function bullseyeFirearmResultLabel(value) {
  const label = String(value || "").trim();
  const lower = label.toLowerCase();
  if (lower.includes("first 900")) return "First 900";
  if (lower.includes("second 900")) return "Second 900";
  if (lower.includes(".22") || lower.includes("22")) return ".22";
  if (lower.includes(".45") || lower.includes("45")) return ".45";
  if (lower.includes("center") || /\bcf\b/i.test(label)) return "CF";
  return label || "Firearm";
}

function bullseyeFirearmDisplayName(value, fallback = "Firearm") {
  const label = String(value || "").trim();
  return label || fallback;
}

function normalizeBullseyeCategoryLabel(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const key = raw.toLowerCase().replace(/[\s-]+/g, "_");
  const looseKey = raw.toLowerCase().replace(/[_-]+/g, " ");
  return BULLSEYE_CATEGORY_LABELS.get(key) || BULLSEYE_CATEGORY_LABELS.get(looseKey) || raw
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function normalizeBullseyeCategories(value) {
  const rawValues = Array.isArray(value)
    ? value
    : String(value || "")
      .split(",")
      .map((item) => item.trim());
  const seen = new Set();
  const labels = [];

  rawValues.forEach((item) => {
    const label = normalizeBullseyeCategoryLabel(item);
    const key = label.toLowerCase();
    if (label && !seen.has(key)) {
      seen.add(key);
      labels.push(label);
    }
  });

  return labels;
}

export function normalizeBullseyeClassificationLabel(value) {
  return normalizeResultClass(value);
}

function bullseyeClassSortIndex(label) {
  const normalized = normalizeBullseyeClassificationLabel(label);
  if (String(label || "").toLowerCase().includes("sharpshooter/marksman")) {
    return BULLSEYE_CLASS_ORDER.indexOf("Sharpshooter");
  }
  const index = BULLSEYE_CLASS_ORDER.indexOf(normalized);
  return index >= 0 ? index : BULLSEYE_CLASS_ORDER.length;
}

function bullseyeOrdinalAwardLabel(index) {
  return BULLSEYE_CLASS_AWARD_LABELS[index] || "";
}

function bullseyeCategorySortIndex(label) {
  const normalized = normalizeBullseyeCategoryLabel(label);
  const index = BULLSEYE_CATEGORY_ORDER.indexOf(normalized);
  return index >= 0 ? index : BULLSEYE_CATEGORY_ORDER.length;
}

function bullseyeAwardRow(row, awardRank, awardText = "") {
  return {
    ...row,
    awardRank,
    awardText: row.isTied && awardText ? "Tie - pending review" : awardText,
  };
}

export function isBullseyeTwentyTwoOnlyRow(row) {
  return normalizeBullseyeCategories(row?.categories || row?.category)
    .some((label) => label.toLowerCase() === "22 only");
}

function withoutBullseyeTwentyTwoOnlyRows(rows = []) {
  return rows.filter((row) => !isBullseyeTwentyTwoOnlyRow(row));
}

function buildBullseyeRankedGroupRows(rows = []) {
  return rankBullseyeRows(rows).map((row) => ({
    ...row,
    awardRank: row.rank,
  }));
}

function buildBullseyeOpenAwardRows(rows = []) {
  return bullseyeAwardCutoff(rows, BULLSEYE_OPEN_AWARD_LABELS.length)
    .map((row) => bullseyeAwardRow(row, row.rank, BULLSEYE_OPEN_AWARD_LABELS[row.rank - 1]));
}

function buildBullseyeSpecialCategoryAwardRows(rows = []) {
  const awards = [];
  const seenWinners = new Set();
  buildBullseyeCategoryBlocks(rows, { includeTwentyTwo: false }).forEach((block) => {
    const label = String(block.categoryLabel || block.title || "").replace(/\s*--.*$/i, "").trim();
    bullseyeAwardCutoff(block.rows || [], 1).forEach((winner) => {
      const winnerKey = `${label}-${winner.rowKey}`;
      if (seenWinners.has(winnerKey)) return;
      seenWinners.add(winnerKey);
      awards.push({
        ...bullseyeAwardRow(winner, 1, `High ${label}`),
        awardText: winner.isTied ? `High ${label} tie - pending review` : `High ${label}`,
      });
    });
  });
  return awards;
}

function buildBullseyeClassBlocks(rows = [], { configuredGroups = separateClassGroups(), addAwardText = false, exclusion = "none", entrantRows = rows, thresholds, showExcludedClassWinners = false } = {}) {
  const groups = new Map();
  rows.forEach((row) => {
    const cls = competingResultClass(row.classification);
    const label = configuredGroups.find(group => group.classes.includes(cls))?.title || cls;
    const groupRows = groups.get(label) || [];
    groupRows.push(row);
    groups.set(label, groupRows);
  });

  return [...groups.entries()]
    .filter(([label]) => !addAwardText || configuredGroups.find(group => group.title === label)?.classAwardsEligible !== false)
    .sort(([left], [right]) => {
      const order = title => {
        const group = configuredGroups.find(item => item.title === title);
        return group ? Math.min(...group.classes.map(bullseyeClassSortIndex)) : bullseyeClassSortIndex(title);
      };
      const classDelta = order(left) - order(right);
      return classDelta !== 0 ? classDelta : left.localeCompare(right);
    })
    .map(([label, groupRows]) => {
      const entrantIds = new Set(entrantRows.filter(row => {
        const cls = competingResultClass(row.classification);
        return (configuredGroups.find(group => group.classes.includes(cls))?.title || cls) === label;
      }).map(row => row.sourceRowKey || row.rowKey));
      const entrantCount = entrantIds.size;
      const awardCount = classAwardCount(entrantCount, thresholds);
      const openWinners = exclusion === "none" ? [] : bullseyeAwardCutoff(rows, exclusion === "open_places" ? 3 : 1);
      const exclusionPlaces = exclusion === "open_places" ? 3 : 1;
      const uncertainWinner = row => row.isTied
        && row.rank + openWinners.filter(other => other.rank === row.rank).length - 1 > exclusionPlaces;
      const excluded = new Set(openWinners.filter(row => !uncertainWinner(row)).map(row => row.rowKey));
      // A tie entirely inside the excluded places cannot change class eligibility.
      const overallTiePending = openWinners.some(row => uncertainWinner(row)
        && groupRows.some(member => member.rowKey === row.rowKey));
      const eligible = rankBullseyeRows(groupRows.filter(row => !excluded.has(row.rowKey)));
      const awards = new Map(eligible.map(row => [row.rowKey, row]));
      return {
      key: `class-${label}`,
      title: `${label.toUpperCase()} -- ALL CATEGORIES`,
      placeLabel: "Class Place",
      ...(addAwardText && thresholds != null ? { entrantCount, awardCount,
        awardSummary: `${entrantCount} ${entrantCount === 1 ? "entrant" : "entrants"} / ${awardCount} class ${awardCount === 1 ? "award" : "awards"}` } : {}),
      // Display filtering never changes entrant counts or the award-recipient calculation above.
      rows: buildBullseyeRankedGroupRows(showExcludedClassWinners ? groupRows : groupRows.filter(row => !excluded.has(row.rowKey))).map((row) => {
        const award = awards.get(row.rowKey);
        const ordinal = addAwardText && award?.rank != null && award.rank <= awardCount ? bullseyeOrdinalAwardLabel(award.rank - 1) : "";
        const text = ordinal ? (overallTiePending ? "Pending overall tie review" : `${ordinal} ${label} - All Categories`) : "";
        const openWinner = openWinners.find(winner => winner.rowKey === row.rowKey);
        const awardNote = !openWinner ? "" : uncertainWinner(openWinner) ? "Pending overall tie review"
          : `${openWinner.rank === 1 ? "Overall winner" : "Overall place award"} - class award excluded`;
        return { ...row, awardText: award?.isTied && text ? "Tie - pending review" : text, awardNote };
      }),
    }; }).filter(block => block.rows.length > 0);
}

function buildBullseyeCategoryBlocks(rows = [], { includeTwentyTwo = true } = {}) {
  const groups = new Map();
  rows.forEach((row) => {
    // Sight awards use the current division, not stale division tags in categories.
    const labels = normalizeBullseyeCategories(row.categories || row.category)
      .filter(label => !/^(open|metallic)( division)?$/i.test(label));
    const division = String(row.division || "").trim().match(/^(open|metallic)(?:[ _-]+division)?$/i)?.[1]?.toLowerCase();
    if (division) {
      labels.push(division === "metallic" ? "Metallic" : "Open");
    }
    labels.forEach((label) => {
      if (!includeTwentyTwo && label.toLowerCase() === "22 only") {
        return;
      }
      const groupRows = groups.get(label) || [];
      groupRows.push(row);
      groups.set(label, groupRows);
    });
  });

  return [...groups.entries()]
    .sort(([left], [right]) => {
      const categoryDelta = bullseyeCategorySortIndex(left) - bullseyeCategorySortIndex(right);
      return categoryDelta !== 0 ? categoryDelta : left.localeCompare(right);
    })
    .map(([label, groupRows]) => ({
      key: `category-${label}`,
      categoryLabel: label,
      title: `${label.toUpperCase()} -- SPECIAL CATEGORY STANDINGS`,
      rows: buildBullseyeRankedGroupRows(groupRows),
    }));
}

function buildBullseyeTwentyTwoBlock(rows = [], { addAwardText = false } = {}) {
  const twentyTwoRows = rows.filter(isBullseyeTwentyTwoOnlyRow);
  return {
    key: "twenty-two-only",
    title: "22 CALIBER ONLY - ALL CATEGORIES",
    rows: buildBullseyeRankedGroupRows(twentyTwoRows).map((row) => {
      const ordinal = addAwardText ? bullseyeOrdinalAwardLabel(row.rank - 1) : "";
      return ordinal ? bullseyeAwardRow(row, row.rank, `${ordinal} 22 Caliber Only - All Categories`) : row;
    }),
    emptyTitle: "No 22 Only aggregate competitors in this section",
  };
}

export function buildBullseyeAwardBlocks(section, awardView, settings) {
  const rows = Array.isArray(section?.rows) ? section.rows : [];
  const entrantRows = Array.isArray(section?.entrantRows) ? section.entrantRows : rows;
  const openRows = withoutBullseyeTwentyTwoOnlyRows(rows);
  if (awardView === "overall") {
    return [
      { key: "overall", title: "STANDARD AGGREGATE -- PLACE STANDINGS", rows: buildBullseyeRankedGroupRows(openRows) },
      ...(rows.some(isBullseyeTwentyTwoOnlyRow) ? [buildBullseyeTwentyTwoBlock(rows)] : []),
    ];
  }
  if (awardView === "class") {
    const classBlocks = buildBullseyeClassBlocks(openRows, { configuredGroups: settings?.standardGroups,
      exclusion: settings?.classAwardExclusion, showExcludedClassWinners: settings?.showExcludedClassWinners === true });
    return classBlocks.length > 0
      ? classBlocks
      : [{ key: "class-empty", title: "CLASS STANDINGS", rows: [], emptyTitle: openRows.length ? "No eligible competitors in these class standings" : "No standard aggregate competitors in this section" }];
  }
  if (awardView === "category") {
    const categoryBlocks = buildBullseyeCategoryBlocks(openRows, { includeTwentyTwo: false });
    return categoryBlocks.length > 0
      ? categoryBlocks
      : [{
        key: "category-empty",
        title: "SPECIAL CATEGORY STANDINGS",
        rows: [],
        emptyTitle: "No special category competitors in this section",
      }];
  }
  if (awardView === "twenty_two") {
    return [buildBullseyeTwentyTwoBlock(rows)];
  }

  const specialCategoryRows = buildBullseyeSpecialCategoryAwardRows(openRows);
  return [
    { key: "open", title: "STANDARD AGGREGATE -- PLACE AWARDS", rows: buildBullseyeOpenAwardRows(openRows) },
    ...(specialCategoryRows.length > 0
      ? [{ key: "special-categories", title: "SPECIAL CATEGORY AWARDS", rows: specialCategoryRows }]
      : []),
    ...buildBullseyeClassBlocks(openRows, { configuredGroups: settings?.standardGroups, addAwardText: true, exclusion: settings?.classAwardExclusion,
      entrantRows: withoutBullseyeTwentyTwoOnlyRows(entrantRows), thresholds: settings?.classAwardThresholds,
      showExcludedClassWinners: settings?.showExcludedClassWinners === true }),
    ...(rows.some(isBullseyeTwentyTwoOnlyRow) ? [buildBullseyeTwentyTwoBlock(rows, { addAwardText: true })] : []),
  ];
}

function buildBullseyeSectionRow(baseRow, scoreSource, rank = 0, tieContext) {
  const categories = normalizeBullseyeCategories(baseRow?.categories || baseRow?.category);
  return {
    sourceRowKey: bullseyeRowKey(baseRow),
    tieContext,
    rowKey: `${bullseyeRowKey(baseRow)}-${scoreSource?.gunGroupId || ""}-${scoreSource?.courseName || ""}-${rank}`,
    competitorName: baseRow?.competitorName || "Competitor",
    competitorNumber: baseRow?.competitorNumber || baseRow?.shooterNumber || baseRow?.number || "",
    classification: baseRow?.classification || baseRow?.classificationName || baseRow?.nraClassification || "",
    division: baseRow?.division,
    outcomeStatus: baseRow?.outcomeStatus,
    isRankEligible: baseRow?.isRankEligible,
    categories,
    category: categories.join(", "),
    totalScore: numberOrZero(scoreSource?.totalScore),
    xCount: numberOrZero(scoreSource?.xCount),
    shotsFired: scoreSource?.shotsFired == null ? undefined : numberOrZero(scoreSource.shotsFired),
    plannedShots: numberOrZero(scoreSource?.plannedShots),
    status: scoreSource?.status || baseRow?.status || "unscored",
    rank,
  };
}

function sectionKindPriority(kind) {
  if (kind === "match") return 0;
  if (kind === "course") return 1;
  if (kind === "firearm") return 2;
  return 3;
}

export function buildBullseyePostedSections(rows = [], settings) {
  const sections = new Map();

  function addSection(sectionKey, sectionDraft, entry) {
    // The API includes enrolled but unscored entrants; count them without ranking them.
    const existing = sections.get(sectionKey) || { ...sectionDraft, rows: [], entrantRows: [] };
    existing.entrantRows.push(entry);
    if (hasBullseyeScoreData(entry)) existing.rows.push(entry);
    sections.set(sectionKey, existing);
  }

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    addSection(
      "match::aggregate",
      {
        key: "match::aggregate",
        title: "Grand Aggregate",
        eyebrow: "Overall Results",
        kind: "match",
        order: 0,
        displayName: "Match Aggregate",
      },
      buildBullseyeSectionRow(row, row, 0, bullseyeTieContext(row, "match::aggregate", "match", settings)),
    );

    (Array.isArray(row?.firearms) ? row.firearms : []).forEach((firearm, firearmIndex) => {
      const firearmLabel = bullseyeFirearmResultLabel(firearm?.firearmTitle);
      const firearmDisplayName = bullseyeFirearmDisplayName(firearm?.firearmTitle, firearmLabel);
      const firearmKey = `firearm::${firearm?.gunGroupId || firearmIndex}`;
      addSection(
        firearmKey,
        {
          key: firearmKey,
          title: `${firearmDisplayName} Aggregate`,
          eyebrow: "Firearm Aggregate",
          kind: "firearm",
          order: 100 + firearmIndex * 10 + 9,
          displayName: `${firearmDisplayName} total`,
        },
        buildBullseyeSectionRow(row, firearm, 0, bullseyeTieContext(row, firearmKey, "firearm", settings)),
      );

      (Array.isArray(firearm?.courses) ? firearm.courses : []).forEach((course, courseIndex) => {
        const courseLabel = normalizeBullseyeCourseName(course?.courseName);
        const courseKey = `course::${firearm?.gunGroupId || firearmIndex}::${course?.courseName || courseIndex}`;
        addSection(
          courseKey,
          {
            key: courseKey,
            title: `${firearmDisplayName} ${courseLabel}`,
            eyebrow: firearmDisplayName,
            kind: "course",
            order: 100 + firearmIndex * 10 + courseIndex,
            displayName: `${firearmDisplayName} · ${courseLabel}`,
          },
          buildBullseyeSectionRow(row, course, 0, bullseyeTieContext(row, courseKey, "course", settings)),
        );
      });
    });
  });

  return [...sections.values()]
    .filter(section => section.rows.length > 0)
    .map((section) => {
      const rankedRows = rankBullseyeRows(section.rows);
      const leader = rankedRows[0] || null;
      const plannedShots = rankedRows.reduce((maxValue, row) => Math.max(maxValue, numberOrZero(row.plannedShots)), 0);
      const twentyTwoOnlyCount = rankedRows.filter(isBullseyeTwentyTwoOnlyRow).length;
      return {
        ...section,
        rows: rankedRows,
        leader,
        competitorCount: rankedRows.length,
        openCompetitorCount: rankedRows.length - twentyTwoOnlyCount,
        twentyTwoOnlyCount,
        plannedShots,
      };
    })
    .sort((left, right) => {
      const priorityDelta = sectionKindPriority(left.kind) - sectionKindPriority(right.kind);
      if (priorityDelta !== 0) return priorityDelta;
      return left.order - right.order;
    });
}

export function buildBullseyeBulletinDocument(match, sections, selectedSection = "all") {
  const prepareBlock = block => ({ ...block,
    children: block.children?.map(prepareBlock),
    rows: block.rows?.map(row => ({ ...row, divisionLabel: sightDivisionLabel(row.division),
      classificationLabel: normalizeBullseyeClassificationLabel(row.classification) === "Unclassified" ? "UNC" : row.classification })),
  });
  return {
    matchId: match.matchId,
    name: match.matchName || match.name || match.title || "Bullseye Match",
    date: formatDate(match.matchDate || match.startDate || match.date),
    location: buildLocationLabel(match) !== "Location not set" ? buildLocationLabel(match) : "",
    programNotes: match.bullseyeResultSettings?.programNotes,
    sections: sections.filter(section => selectedSection === "all" || section.key === selectedSection)
      .map(section => ({ ...section,
        participationLabel: participationCountsText(section.competitorCount, section.twentyTwoOnlyCount),
        blocks: buildBullseyeAwardBlocks(section, "bulletin", match.bullseyeResultSettings).map(prepareBlock) })),
  };
}

export function bullseyeRowKey(row, index = 0) {
  return String(row?.sk || row?.entryId || row?.competitorId || row?.competitorExternalId || `row-${index}`);
}
